import React from 'react'
import clsx from 'clsx'
import { MdDragHandle } from 'react-icons/md'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { useDrag, useDrop } from 'react-dnd'
import { CloseCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Tooltip from '@SonarCX/components/atoms/Tooltip'
import FieldIcon from '../../../FieldIcon'

import { selectCustomFields } from '@SonarCX/slices/customFields'
import useStyles from './styles'

type GroupFieldProps = {
  id: string
  isFirst: boolean
  isLast: boolean
  onDelete: () => void
  groupID: string
  onMove: (source: string, target: string, above: boolean) => void
}

const GroupField: React.FC<GroupFieldProps> = ({
  id,
  isFirst,
  isLast,
  onDelete,
  groupID,
  onMove
}) => {
  const theme = useTheme()

  const { customFields } = useAppSelector(selectCustomFields)

  const field = React.useMemo(
    () => customFields.find(field => field.id === id),
    [customFields, id]
  )

  const ITEM_TYPE = `${groupID}-custom-field`

  const [, dragRef, dragPreviewRef] = useDrag({
    item: { type: ITEM_TYPE, id }
  })

  const [{ isOver: isOverTop }, dropTopRef] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: any) => onMove(item.id, id, true),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [{ isOver: isOverBottom }, dropBottomRef] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: any) => onMove(item.id, id, false),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const classes = useStyles()

  return (
    <>
      <div
        ref={dropTopRef}
        className={clsx(
          classes.dropZone,
          isOverTop && (isFirst ? classes.dropZoneTop : classes.dropZoneMiddle)
        )}
      />

      <div className={classes.root}>
        <div ref={dragPreviewRef} className={classes.dragPreview}>
          <div ref={dragRef} className={classes.dragHandle}>
            <MdDragHandle className={classes.dragIcon} />
          </div>

          <div className={classes.name}>
            {field ? (
              <>
                <FieldIcon
                  type={field.field_type}
                  size={theme.size.base}
                  className={classes.fieldIcon}
                />

                <Tooltip title={field.description ?? field.title}>
                  <span>{field.title}</span>
                </Tooltip>
              </>
            ) : (
              <span style={{ color: 'gainsboro' }}>Unknown Field</span>
            )}
          </div>
        </div>

        <CloseCircle
          size={theme.size.base}
          onClick={onDelete}
          className={classes.closeIcon}
        />
      </div>

      {isLast && (
        <div
          ref={dropBottomRef}
          className={clsx(
            classes.dropZone,
            isOverBottom && classes.dropZoneBottom
          )}
        />
      )}
    </>
  )
}

export default GroupField
